import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import { useI18n } from '../../i18n/I18nContext';
import useScrollFade from '../../hooks/useScrollFade';
import { EM_DASH, formatMoney, formatRelative } from '../../utils/datetime';
import { humanize } from '../../utils/labels';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';

// Order statuses are the API's enum; the label always comes from the dictionary.
const STATUS_TONE = {
  COMPLETED: 'success',
  PENDING: 'warning',
  PROCESSING: 'warning',
  FAILED: 'danger',
  CANCELLED: 'danger',
  REFUNDED: 'info',
};

// The tones an avatar can take, picked from a hash of the student so it stays the same everywhere.
const AVATAR_TONES = ['brand', 'success', 'info', 'purple', 'coral', 'teal'];

function toneOf(seed) {
  const text = String(seed || '');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

function initialsOf(name) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

/** "Payme" / "Click" for the provider enums; a provider with no word yet in sentence case, never the enum. */
function useProviderLabel() {
  const { t } = useI18n();
  return (raw) => {
    const key = String(raw || '').trim();
    if (!key) return EM_DASH;
    const label = t(`dashboard.provider.${key.toUpperCase()}`);
    return label.startsWith('dashboard.provider.') ? humanize(key) : label;
  };
}

/** The newest orders of the account's scope, a table that stacks into cards on a phone. */
function RecentOrders({ orders, loading = false }) {
  const { t } = useI18n();
  const providerLabel = useProviderLabel();
  // Where the table is wider than its card the cut edge fades, so a last column is never lost silently.
  const { ref: scrollRef, fade } = useScrollFade({ watch: `${loading}-${orders.length}` });

  return (
    <section className="dash-orders" aria-labelledby="dash-orders-title" aria-busy={loading}>
      <div className="section-head">
        <h2 className="section-head__title" id="dash-orders-title">{t('dashboard.recentActivity')}</h2>
        <Link to="/sales" className="section-head__action">
          {t('dashboard.viewAllOrders')}
          <ArrowRight className="dash-action-arrow" size={14} aria-hidden="true" />
        </Link>
      </div>

      {loading ? (
        <div className="ui-table-card dash-orders__skeleton" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="ui-skel dash-orders__skel" />)}
        </div>
      ) : orders.length === 0 ? (
        <InlineEmpty
          surface="card"
          kind="empty"
          title={t('dashboard.emptyOrders.title')}
          description={t('dashboard.emptyOrders.desc')}
        />
      ) : (
        <div className="ui-table-card">
          <div ref={scrollRef} className={`ui-table-scroll ${fade}`.trim()}>
            <table className="ui-table ui-table--stack dash-orders__table">
              <thead>
                <tr>
                  <th>{t('dashboard.table.student')}</th>
                  <th>{t('dashboard.table.when')}</th>
                  <th className="ui-num">{t('dashboard.table.amount')}</th>
                  <th>{t('dashboard.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, i) => {
                  const student = order.user?.name || order.student?.name || '';
                  return (
                    <tr key={order.id || i}>
                      <td className="dash-orders__who">
                        <div className="ui-cell-person">
                          <span
                            className="ui-avatar"
                            data-tone={toneOf(order.user?.id || order.userId || student)}
                            aria-hidden="true"
                          >
                            {initialsOf(student) || EM_DASH}
                          </span>
                          <div className="ui-cell-person__text">
                            <span className="ui-cell-person__name">{student || EM_DASH}</span>
                            <span className="ui-cell-person__sub">{order.course?.title || EM_DASH}</span>
                          </div>
                        </div>
                      </td>
                      <td data-label={t('dashboard.table.when')} className="dash-orders__when">
                        {formatRelative(order.createdAt)}
                      </td>
                      <td data-label={t('dashboard.table.amount')} className="ui-num dash-orders__money">
                        <div className="dash-orders__amount">
                          <span className="dash-orders__sum">{formatMoney(order.amount, order.currency || undefined)}</span>
                          <span className="dash-orders__via">{providerLabel(order.paymentMethod || order.provider)}</span>
                        </div>
                      </td>
                      <td data-label={t('dashboard.table.status')} className="dash-orders__state">
                        {order.status
                          ? <StatusBadge status={order.status} tone={STATUS_TONE[String(order.status).toUpperCase()]} />
                          : EM_DASH}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export default RecentOrders;
